/**
 * Shared simulation types. The sim package imports nothing from the DOM and
 * allocates nothing inside step(); these types describe the data that flows in
 * (InputFrame, CourseData) and the enums that describe what flows out.
 */

/** Simulation rate in ticks per second. */
export const HZ = 120;
/** Seconds per tick. */
export const DT = 1 / HZ;
/** Collision substeps per tick. */
export const SUBSTEPS = 4;
/** Seconds per collision substep. */
export const SUB_DT = DT / SUBSTEPS;

/** One tick of player intent. Held state only; edges are derived inside the sim. */
export interface InputFrame {
  /** Left stick X after deadzone and rescale, -1..1. Keys give -1, 0, 1. */
  moveX: number;
  /** Left stick Y after deadzone and rescale, -1..1, up is positive. */
  moveY: number;
  /** Right stick X, -1..1. Positive tilts the torso forward (toward +x). */
  tiltX: number;
  /** Right stick Y, -1..1. */
  tiltY: number;
  /** Arm extension amount, 0..1, after the trigger deadzone and reach curve. */
  reach: number;
  /** Arm contraction amount, 0..1. Read and drawn in v0, otherwise unused until v4 (the pull-up). */
  pull: number;
  jump: boolean;
  tuck: boolean;
  pause: boolean;
  restart: boolean;
}

export function createInputFrame(): InputFrame {
  return {
    moveX: 0,
    moveY: 0,
    tiltX: 0,
    tiltY: 0,
    reach: 0,
    pull: 0,
    jump: false,
    tuck: false,
    pause: false,
    restart: false,
  };
}

export function copyInputFrame(dst: InputFrame, src: InputFrame): void {
  dst.moveX = src.moveX;
  dst.moveY = src.moveY;
  dst.tiltX = src.tiltX;
  dst.tiltY = src.tiltY;
  dst.reach = src.reach;
  dst.pull = src.pull;
  dst.jump = src.jump;
  dst.tuck = src.tuck;
  dst.pause = src.pause;
  dst.restart = src.restart;
}

/**
 * Course JSON, version 0 or 1. Units are meters. `x, y` of a solid is its bottom-left corner.
 * Version 1 adds `box` (a hurdle whose top the hands plant on) and `pipe` (a low obstacle to slide
 * under); both collide as solids.
 */
export interface CourseData {
  version: 0 | 1 | 2;
  name: string;
  /** Display name for menus and the scoreboard (version 2; falls back to `name`). */
  title?: string;
  /** Spawn point. On a timed course it sits in the warm-up area, left of `startLine`. */
  start: { x: number; y: number };
  /**
   * Timed courses (version 2): the clock starts when the feet cross this x moving right, and stops at
   * `finishLine`. Going back behind the start line cancels a run, so the warm-up area is free play.
   */
  startLine?: number;
  finishLine?: number;
  /** Respawn points in course order; `name` labels a section for the section menu. */
  respawns: { x: number; y: number; name?: string }[];
  marks: { name: string; x: number }[];
  entities: CourseEntity[];
}

export type CourseEntity =
  | { type: "solid"; x: number; y: number; w: number; h: number }
  | { type: "box"; x: number; y: number; w: number; h: number }
  | { type: "pipe"; x: number; y: number; w: number; h: number }
  | { type: "water"; x: number; y: number; w: number; h: number }
  | { type: "bar"; x: number; y: number }
  /** A salmon ladder: `rungs` pairs of pegs from `y` up, `spacing` apart, holding one bar that pops up them. */
  | { type: "ladder"; x: number; y: number; rungs: number; spacing: number };

/** Character states. Numbers so the state object stays a flat record of numbers. */
export const S = {
  Idle: 0,
  Run: 1,
  Skid: 2,
  Jump: 3,
  Fall: 4,
  Hang: 5,
  Swing: 6,
  Fail: 7,
  /** v1 labels the systems produce (plan section 7.7), appended so the v0 numbers do not move. */
  Plant: 8,
  Slide: 9,
  Roll: 10,
  /** Hanging from a ledge by the hands (DESIGN.md 4.9), a rest state the mantle leaves through Plant. */
  Ledge: 11,
} as const;
export type StateId = (typeof S)[keyof typeof S];
export const STATE_NAMES = [
  "Idle",
  "Run",
  "Skid",
  "Jump",
  "Fall",
  "Hang",
  "Swing",
  "Fail",
  "Plant",
  "Slide",
  "Roll",
  "Ledge",
] as const;

/** Solid classes: what a solid is besides a collider. */
export const SolidClass = {
  Solid: 0,
  Box: 1,
  Pipe: 2,
} as const;

/** Move grades. Shared by every graded move from v0 onward. */
export const Grade = {
  None: 0,
  Miss: 1,
  Ok: 2,
  Good: 3,
  Perfect: 4,
} as const;
export type GradeId = (typeof Grade)[keyof typeof Grade];
export const GRADE_NAMES = ["-", "MISS", "OK", "GOOD", "PERFECT"] as const;

/** What a Graded event grades. */
export const GradeKind = {
  Catch: 0,
  Release: 1,
  Pump: 2,
  Vault: 3,
  Slide: 4,
  Roll: 5,
  /** A salmon ladder climb: a PERFECT duck at the bottom (legacy tapes use the apex pull/kip). */
  Ladder: 6,
  /** A mantle over a ledge: Jump or Pull from the hang, graded on how quickly it followed the grab. */
  Mantle: 7,
} as const;
export type GradeKindId = (typeof GradeKind)[keyof typeof GradeKind];
export const GRADE_KIND_NAMES = [
  "catch",
  "release",
  "pump",
  "vault",
  "slide",
  "roll",
  "ladder",
  "mantle",
] as const;

/** Why a link run ended. */
export const LinkEnd = {
  /** A backward bottom crossing while attached. */
  Backswing: 0,
  /** A dead hang that lasted hangBreakMs. */
  Hang: 1,
  /** The run ended on a landing: not a break, the count stands. */
  Ground: 2,
  /** Water. */
  Fail: 3,
  /** Restart. */
  Restart: 4,
} as const;
export const LINK_END_NAMES = ["backswing", "hang", "ground", "fail", "restart"] as const;

/** Event kinds emitted by step(). */
export const Ev = {
  /** a = from state, b = to state */
  StateChanged: 0,
  /** Impulse. a = jump velocity, b = 1 if from coyote, 2 if buffered */
  Jumped: 1,
  /** Impulse. a = vertical velocity after the cut */
  JumpCut: 2,
  /** a = impact speed (positive, m/s), b = horizontal speed */
  Landed: 3,
  /** Impulse. a = bar id, b = omega after the catch, c = L at attach */
  Gripped: 4,
  /** Impulse. a = kind (0 plain, 1 kick), b = speed, c = theta in degrees */
  Released: 5,
  /** a = grade, b = offset to the bottom crossing in ms (signed, negative = early) */
  Pump: 6,
  /** Impulse. */
  Failed: 7,
  /** Impulse. a = respawn index */
  Respawned: 8,
  /** Impulse. a = start impulse velocity */
  RunStart: 9,
  /** a = mark index */
  MarkPassed: 10,
  /** a = swing direction after crossing (+1 forward, -1 back) */
  BottomCrossed: 11,
  /** a = GradeKind, b = grade, c = the graded value (ms, degrees, or the catch lead in ms) */
  Graded: 12,
  /** a = link run count after this link, b = bar id, c = 1 if a new best run */
  Link: 13,
  /** a = the run count that ended, b = LinkEnd reason */
  LinkBroken: 14,
  /** a = obstacle solid id, b = impact speed */
  Hit: 15,
  /** Impulse. a = box solid id, b = push velocity */
  Planted: 16,
  /** a = box solid id; b = 1 when the plant was a mantle over a ledge, c = that mantle's grade */
  Mounted: 17,
  /** Impulse (a PERFECT roll adds speed). a = grade, b = pitch at touchdown in degrees, c = spin at touchdown in rad/s */
  Rolled: 18,
  /** Impulse. a = drop height, b = speed after */
  HardLanding: 19,
  /** a = freeze length in ticks */
  Frozen: 20,
  /** Timed course: the feet crossed the start line. a = the crossing as a fractional tick */
  RunStarted: 21,
  /** Timed course: a run was abandoned. a = reason (0 back behind the start line, 1 restart, 2 respawned behind it) */
  RunCancelled: 22,
  /** Timed course: a respawn point was passed during a run. a = respawn index, b = split time, s */
  Checkpoint: 23,
  /** Timed course: the feet crossed the finish line. a = run time, s; b = falls in the run */
  RunFinished: 24,
  /** Impulse. A salmon ladder pop: the bar seated on the rung above. a = ladder id, b = the rung it sits on now, c = grade */
  Popped: 25,
  /** Impulse. The hands took a ledge. a = solid id, b = horizontal speed at the grab, c = which way the wall is (+1 at +x) */
  LedgeGrabbed: 26,
  /** Impulse. The mantle began from a hang. a = solid id, b = grade, c = ms from the grab to the press */
  Mantled: 27,
  /** Reach let go of a ledge: the body falls from the hang. a = solid id */
  LedgeDropped: 28,
  /** Impulse. A PERFECT slide adds speed. a = pipe solid id, b = added speed, c = resulting vx */
  SlideBoost: 29,
} as const;
export type EvId = (typeof Ev)[keyof typeof Ev];
export const EV_NAMES = [
  "StateChanged",
  "Jumped",
  "JumpCut",
  "Landed",
  "Gripped",
  "Released",
  "Pump",
  "Failed",
  "Respawned",
  "RunStart",
  "MarkPassed",
  "BottomCrossed",
  "Graded",
  "Link",
  "LinkBroken",
  "Hit",
  "Planted",
  "Mounted",
  "Rolled",
  "HardLanding",
  "Frozen",
  "RunStarted",
  "RunCancelled",
  "Checkpoint",
  "RunFinished",
  "Popped",
  "LedgeGrabbed",
  "Mantled",
  "LedgeDropped",
  "SlideBoost",
] as const;

/** Run phases on a timed course (`SimState.run`). */
export const Run = {
  /** In the warm-up area or anywhere before a crossing of the start line: the clock is not running. */
  Ready: 0,
  Running: 1,
  Finished: 2,
} as const;

/** Why a run was cancelled (`Ev.RunCancelled` a). */
export const RunCancel = {
  BehindStart: 0,
  Restart: 1,
  RespawnedBehindStart: 2,
} as const;

/** Events that mark a deliberate velocity discontinuity on their tick. */
export const IMPULSE_EVENTS: ReadonlySet<number> = new Set<number>([
  Ev.Jumped,
  Ev.JumpCut,
  Ev.Gripped,
  Ev.Released,
  Ev.Failed,
  Ev.Respawned,
  Ev.RunStart,
  Ev.Planted,
  Ev.HardLanding,
  Ev.Rolled,
  Ev.Popped,
  Ev.LedgeGrabbed,
  Ev.Mantled,
  Ev.SlideBoost,
]);
